import React from 'react'
import { useTranslation } from 'react-i18next'

import DataIntegrationsProvider from 'components/DataIntegrations/DataIntegrationsProvider'
import { useMediaQuery } from 'hooks/useMediaQuery'
import SentinelChatProvider, { useSentinelChat } from './SentinelChatProvider'
import Transcript from './Transcript'
import Composer from './Composer'
import SentinelLibrary from './SentinelLibrary'
import CustomerInspector from './CustomerInspector'
import Drawer from './Drawer'

/**
 * Sentinel — a single-column chat embedded in the app's Layout. `.fd-v2` scopes
 * the design tokens. The library (History/Saved) docks as a collapsible left
 * column, toggled by the single header control. The customer drill-down opens
 * as a right overlay drawer, scoped to the Sentinel content area so it never
 * slips under the app nav.
 */
function ChatShell() {
  const { t } = useTranslation()
  const { ui, toggleLibrary, newConversation } = useSentinelChat()
  const compact = useMediaQuery('(max-width: 991px)')

  return (
    <div className="fd-chat">
      {!compact && ui.libraryOpen && (
        <aside className="fd-lib-dock">
          <div className="fd-lib-dock__header">
            <span className="fd-eyebrow">{t('Sentinel.V2.panelTitle')}</span>
          </div>
          <SentinelLibrary />
        </aside>
      )}

      <div className="fd-chat__col">
        <div className="fd-chat__header">
          <button
            className="fd-btn fd-btn--ghost fd-btn--icon fd-btn--sm"
            aria-label={
              ui.libraryOpen ? t('Sentinel.V2.collapsePanel') : t('Sentinel.V2.showPanel')
            }
            aria-expanded={ui.libraryOpen}
            onClick={toggleLibrary}
          >
            <i
              className={`fa-solid fa-chevron-${ui.libraryOpen ? 'left' : 'right'}`}
              aria-hidden="true"
            />
          </button>
          <span className="fd-product-icon fd-product-icon--sm" aria-hidden="true">
            Se
          </span>
          <span className="fd-chat__title">Sentinel</span>
          <div style={{ flex: 1 }} />
          <button className="fd-btn fd-btn--ghost fd-btn--sm" onClick={newConversation}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> {t('Sentinel.V2.new')}
          </button>
        </div>

        <Transcript />

        <div className="fd-composer-dock">
          <div className="fd-composer-dock__inner">
            <Composer />
          </div>
        </div>
      </div>

      {compact && (
        <Drawer
          open={ui.libraryOpen}
          onClose={toggleLibrary}
          side="left"
          title={t('Sentinel.V2.panelTitle')}
        >
          <SentinelLibrary onSelect={toggleLibrary} />
        </Drawer>
      )}

      <CustomerInspector />
    </div>
  )
}

export default function SentinelWorkspace() {
  return (
    <div className="fd-v2 fd-sentinel-page">
      <DataIntegrationsProvider>
        <SentinelChatProvider>
          <ChatShell />
        </SentinelChatProvider>
      </DataIntegrationsProvider>
    </div>
  )
}
